import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'

import { MinusCircleIcon } from 'images'

import QualifierDropdown from './QualifierDropdown'
import { useRangeLocations, useRangeLocationsDispatch } from './RangeLocationsContextProvider'
import { actions, getPreferredRangeUnit } from './rangeLocationsReducer'
import RadiusInput from './RadiusInput'

const countries = require('../countries.json')

function Location({ location }) {
  const {
    data: { key, name, region, type: locationType },
    qualifier,
    radius,
  } = location
  const dispatch = useRangeLocationsDispatch()

  const displayName = useMemo(() => {
    let display = name
    if (region && region !== name) {
      display += `, ${region}`
    }
    return display
  }, [name, region])

  const setQualifier = useCallback(
    (qualifier) => {
      dispatch({ type: actions.setQualifier, payload: { locationKey: key, qualifier } })
    },
    [key, dispatch],
  )

  const setRadius = useCallback(
    (radius) => {
      dispatch({
        type: actions.setLocationRadius,
        payload: { locationKey: key, radius: { value: radius, unit: getPreferredRangeUnit() } },
      })
    },
    [key, dispatch],
  )

  const removeLocation = useCallback(() => {
    dispatch({ type: actions.removeLocation, payload: { locationKey: key } })
  }, [key, dispatch])

  return (
    <div className="range-locations_summary__location">
      <div className="range-locations_summary__location__inner">
        <QualifierDropdown
          className="range-locations_summary__location__qualifier"
          value={qualifier}
          onChange={(value) => setQualifier(value)}
        />
        <span className="range-locations_summary__location-name">{displayName}</span>
        {(locationType === 'city' || locationType === 'place') && (
          <RadiusInput defaultValue={radius.value} unit={radius.unit} onChange={setRadius} />
        )}
      </div>
      <i
        title="remove"
        className="range-locations_summary__location-remove"
        onClick={removeLocation}
      >
        <MinusCircleIcon />
      </i>
    </div>
  )
}

Location.propTypes = {
  location: PropTypes.shape({
    data: PropTypes.shape({
      key: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      region: PropTypes.string,
      country_code: PropTypes.string,
    }),
    qualifier: PropTypes.string.isRequired,
    radius: PropTypes.shape({
      value: PropTypes.number,
      unit: PropTypes.oneOf(['miles', 'kilometers']),
    }),
  }),
}

function Country({ countryName, countryGroupName, locations }) {
  return (
    <div className="range-locations_summary__country">
      <h6 className="range-locations_summary__country__name">{countryGroupName || countryName}</h6>
      {locations.map((location) => (
        <Location key={location.data.key} location={location} />
      ))}
    </div>
  )
}
Country.propTypes = {
  countryName: PropTypes.string,
  countryGroupName: PropTypes.string,
  locations: PropTypes.array.isRequired,
}

export default function LocationsSummary() {
  const { locations } = useRangeLocations()
  const { includedList, excludedList } = locations.reduce(
    (acc, location) => {
      const { qualifier } = location
      // Determine the type of location
      const includedList = acc.includedList
      const excludedList = acc.excludedList
      const list = qualifier === 'include' ? includedList : excludedList

      list.push(location)

      return acc
    },
    { includedList: [], excludedList: [] },
  )

  const combinedLocations = [...includedList, ...excludedList]

  const { groupedCountries, groupedCountryGroups } = useMemo(() => groupLocations(combinedLocations), [combinedLocations])

  return (
    <div
      className={cn('range-locations_summary', {
        'range-locations_summary--empty': !locations.length,
      })}
    >
      {/* Render grouped countries */}
      {groupedCountries.map((country) => (
        <Country
          key={country.groupName}
          countryName={country.groupName}
          locations={country.locations}
        />
      ))}

      {/* Render grouped country groups */}
      {groupedCountryGroups.map((countryGroup) => (
        <Country
          key={countryGroup.groupName}
          countryGroupName={countryGroup.groupName}
          locations={countryGroup.locations}
        />
      ))}
    </div>
  )
}

/**
 * @typedef {object} LocationGroup
 * @property {string} groupName - Display name for the group (country or group name)
 * @property {object[]} locations - List of locations under that group
 */

/**
 * Groups locations for display
 * @param {object[]} locations
 * @returns {{ groupedCountries: LocationGroup[], groupedCountryGroups: LocationGroup[] }}
 */
function groupLocations(locations) {
  return locations.reduce(
    (acc, item) => {
      const location = item.data
      const isCountryGroupLike =
        location.is_worldwide === true ||
        location.type === 'country_group' ||
        (Array.isArray(location.country_codes) && location.country_codes.length > 1)

      const countryName = location.country_name || countries[location.country_code]?.name || ''

      if (isCountryGroupLike) {
        // Group by group name
        const existingGroup = acc.groupedCountryGroups.find(
          (group) => group.groupName === location.name,
        )

        if (existingGroup) {
          existingGroup.locations.push(item)
        } else {
          acc.groupedCountryGroups.push({
            groupName: location.name,
            locations: [item],
          })
        }
      } else if (location.country_code) {
        // Group under country
        const existingCountry = acc.groupedCountries.find(
          (group) => group.groupName === countryName,
        )

        if (existingCountry) {
          existingCountry.locations.push(item)
        } else {
          acc.groupedCountries.push({
            groupName: countryName,
            locations: [item],
          })
        }
      } else {
        // Fallback: place in its own group by name
        acc.groupedCountryGroups.push({
          groupName: location.name,
          locations: [item],
        })
      }

      return acc
    },
    { groupedCountries: [], groupedCountryGroups: [] },
  )
}
